import clsx from 'clsx';
import { FiLock, FiUserPlus } from 'react-icons/fi';

import { Toggle } from '@/components';
import { GATE_LABEL, type GateReason } from '@/permissions/permission-gates';

import { usePermissionCompare } from '../../PermissionCompareContext';
import { getPermissionMeta } from '../../permissions.ui';

import s from './PermissionRow.module.scss';

interface Props {
  name: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  /** Set when the permission is out of reach; see `GateReason`. */
  gate?: GateReason | null;
  /**
   * Set when the row is shown but must not move for this viewer — their own permissions, a
   * role at or above theirs, a permission they do not hold themselves, or one only the owner
   * group grants. Unlike a gate it says nothing about the plan, so the text stays at full
   * strength; the reason is the row's hover text.
   */
  lockedReason?: string;
  /**
   * Set when the row can be unlocked — by a higher plan, or by moving its person into a
   * management seat: the gate label becomes the button that offers it.
   */
  onUnlock?: (anchor: HTMLElement) => void;
}

const GATE_ICON: Record<GateReason, typeof FiLock> = {
  plan: FiLock,
  seat: FiUserPlus,
};

export default function PermissionRow({
  name,
  enabled,
  onChange,
  gate = null,
  lockedReason,
  onUnlock,
}: Props) {
  const meta = getPermissionMeta(name);
  const rolePermissions = usePermissionCompare();
  const isOverride = rolePermissions !== undefined && enabled !== rolePermissions.has(name);
  const GateIcon = gate ? GATE_ICON[gate] : null;
  const disabled = !!lockedReason;

  // The hover text sits on the row: `Toggle` hands `title` to its hidden input.
  return (
    <div className={clsx(s.row, gate && s.gated)} title={lockedReason}>
      <div className={s.text}>
        <div className={s.titleRow}>
          <span className={s.title}>{meta.title}</span>
          {meta.sensitive && <span className={s.sensitive}>Sensitive</span>}
          {/* An override is a deviation from the role the person can be pulled back to;
              a gated row cannot deviate from anything, so the two never both apply. */}
          {isOverride && !gate && <span className={s.override}>Override</span>}
          {gate &&
            GateIcon &&
            (onUnlock ? (
              <button
                type="button"
                className={clsx(s.gate, s.gateAction)}
                aria-haspopup="dialog"
                onClick={(e) => onUnlock(e.currentTarget)}
              >
                <GateIcon className={s.gateIcon} aria-hidden="true" />
                {gate === 'plan' ? 'Upgrade to Unlock' : GATE_LABEL[gate]}
              </button>
            ) : (
              <span className={s.gate}>
                <GateIcon className={s.gateIcon} />
                {GATE_LABEL[gate]}
              </span>
            ))}
        </div>
        {meta.description && <span className={s.description}>{meta.description}</span>}
      </div>
      {/* A gated row has no state to show: the chip is its only control. */}
      {!gate && (
        <Toggle
          id={name}
          checked={enabled}
          disabled={disabled}
          className={clsx(disabled && s.gatedToggle)}
          onChange={(e) => onChange(e.target.checked)}
        />
      )}
    </div>
  );
}
